// ── imports explicites (C4 — générés par esm-codemod --analyze, maintenus à la main) ──
import { useCallback, useEffect, useRef, useState } from "../00-boot.jsx";
import { langPick, profilLieuChoisi, safetyTextIssue, texteRefuse } from "../01-taxonomy-cities.jsx";

//  (B-MOTS, 02/11/2026) la phrase d'un nom refusé (par l'appareil avant l'envoi, ou par la base) — sans menace ni pénalité
const nomRefuseTexte=(mot,lang)=>langPick(lang,`« ${mot} » n'est pas accepté dans un nom sur LANAC. Votre profil n'a pas été modifié.`,`“${mot}” is not allowed in a name on LANAC. Your profile was not changed.`,`„${mot}“ ist in einem Namen auf LANAC nicht erlaubt. Ihr Profil wurde nicht geändert.`,`«${mot}» não é permitido num nome no LANAC. O seu perfil não foi alterado.`);
import { DEMO_USERS, tx } from "../04-i18n-dict.jsx";
import { FEED_PROFILE_COLS, lanacProfilDeBase, profileFromDB, profileToDB } from "../05-ui-atoms-theme.jsx";
import { deleteFromR2, ensureStoredAvatar, r2KeyOf } from "../06-images-upload.jsx";
import { CACHE_USERS_CACHE_KEY, CACHE_USERS_SYNC_KEY, REPORT_LEAN_SELECT, fetchTableDelta, lanacFlagsFirstBootWait, lanacProfilesLazyOn, listeAJour, reportFromDB, validEmail } from "../07-trust-moderation.jsx";
import { cleanAuthUrl, clearPendingSignupMeta, lanacEpoqueSession, lanacSupabaseClient, mergeOnboardingState, readOnboardingState, readPendingSignupMeta } from "../08-supabase-client.jsx";
//  (B-HORS-LIGNE-1, 03/11/2026) la phrase d'un geste de profil tenté sur la carte (profil frais pas encore arrivé, ou hors ligne)
const profilProvisoireTexte=lang=>(typeof navigator!=="undefined"&&navigator.onLine===false)
  ?tx("tax.hors-ligne-votre-profil_233751",lang)
  :tx("tax.votre-profil-se-met-a-jo_7ede46",lang);
// ── Tranche 4c · useAdminData : annuaire profils (cache) + signalements + MAJ/restauration profil ─
/** @param {any} me @param {{setMe?:Function, notify?:Function, lang?:string, setPendingSignup?:Function, setAuthEmailStatus?:Function}} [ports]
 *  Ports : setMe/setPendingSignup/setAuthEmailStatus (useSession), notify (App, remonté au-dessus de CE
 *  hook pour éviter la TDZ), lang (useUiPrefs) — tous déclarés AVANT l'appel de useAdminData (C3 + D1). */
function useAdminData(me, ports={}){
  const {setMe,notify,lang,setPendingSignup,setAuthEmailStatus}=ports;
  const [allUsers,setAllUsers]=useState(()=>{
    try{
      const saved=JSON.parse(localStorage.getItem("lanac:users")||"null");
      if(!(Array.isArray(saved)&&saved.length)) return DEMO_USERS;
      // EGRESS P1c · annuaire paresseux : un appareil qui a encore l'ANNUAIRE COMPLET en cache
      // (armé après coup) ne doit pas le garder en mémoire — on ne conserve que la queue
      // récente (l'ordre du cache suit l'ordre des merges : les derniers vus sont en fin).
      // `me` n'est pas connu ici : son profil est réinjecté par la restauration de session.
      if(lanacProfilesLazyOn()&&saved.length>300) return saved.slice(-300);
      return saved;
    }catch{return DEMO_USERS;}
  });
  useEffect(()=>{try{
    // EGRESS P1c : à ON (non-admin), le cache est BORNÉ (LRU ~300, `me` toujours conservé) —
    // il redevient ce qu'il aurait toujours dû être : les profils qu'on a CROISÉS, pas la table.
    const lazyCap=lanacProfilesLazyOn()&&me?.role!=="Admin"&&allUsers.length>300;
    const toSave=lazyCap?[...allUsers.filter(u=>u.id===me?.id),...allUsers.filter(u=>u.id!==me?.id).slice(-299)]:allUsers;
    localStorage.setItem("lanac:users",JSON.stringify(toSave));
  }catch{}},[allUsers]);
  // BLOC1-2 · état à jour de allUsers hors-rendu (egress : ne jamais relire la table
  //   pour un merge). Init AVEC la valeur (fidèle à l'original : useRef(allUsers), pas null).
  const allUsersRef=useRef(allUsers);
  useEffect(()=>{allUsersRef.current=allUsers;},[allUsers]); // garde le ref synchro (filtre egress du merge convos)
  const [reports,setReports]=useState([]);
  useEffect(()=>{
    const client=lanacSupabaseClient();
    if(!client||!me?.id){setReports([]);return;}
    // Borne A5 : RLS = reports où je suis impliqué ; plafond 300, récents d'abord.
    // ⭐ Egress (20/07/2026) : ce chemin pesait 47 229 o au boot = 21 % du budget, en
    //  `select("*")`, pour TOUS les utilisateurs. Or les gros consommateurs (openReports,
    //  trustAdminRows, la frise de litige) sont TOUS dans AdminView ;
    //  hors admin, seules 13 colonnes sont réellement lues. On sert donc la projection
    //  MAIGRE au non-admin (−40,5 %, −20 149 o mesurés) et `*` à l'admin, qui affiche le
    //  détail complet. Motif identique à FEED_LISTING_SELECT (Feed-P1b) : ne télécharger
    //  que ce qu'on lit. Aucun changement de comportement — cf. la justification colonne
    //  par colonne au-dessus de REPORT_LEAN_SELECT (07-trust-moderation.jsx).
    const _cols=me?.role==="Admin"?"*":REPORT_LEAN_SELECT;
    //  (04/11/2026, B-DEMARRAGE-1) LA LISTE À JOUR pour un MEMBRE (listeAJour, la projection maigre) : seules les lignes changées repartent ;
    //   l'administrateur garde sa lecture complète (`*`). Une réponse douteuse ou une erreur : la lecture d'avant, telle quelle (ordre ts ↓ puis id).
    //  (revue d'architecte APRÈS) un chargement DÉPASSÉ (membre ou rôle changé — la carte puis le profil frais) ne pose plus rien : un chemin
    //   membre qui arriverait après le chemin administrateur laisserait l'administration sur la projection maigre.
    let cancelled=false;
    const poser=data=>{ if(!cancelled) setReports(data.map(reportFromDB).filter(Boolean)); };
    const lireAvant=()=>client.from("reports").select(_cols).order("ts",{ascending:false}).order("id",{ascending:true}).limit(300).then(({data,error})=>{
      if(!error&&Array.isArray(data))poser(data);
    });
    //  (04/11/2026, B-DEMARRAGE-1c) l'administration aussi : sa liste COMPLÈTE (`*`, ce que l'écran d'administration lit) par
    //   reports_admin_a_jour — sa propre liste gardée, une borne à sa taille (300 lignes complètes ~320 Ko). Repli : `*`, tel quel.
    //  (revue d'architecte APRÈS 1c) le chemin administrateur se juge comme la BASE (reports_admin_a_jour lit is_admin, pas le rôle) : un rôle
    //   « Admin » sans is_admin garde la lecture d'avant (jamais une liste vide) ; et qui n'est plus administrateur ne garde pas la liste COMPLÈTE.
    const _adminBase=me?.role==="Admin"&&me?.isAdmin===true;
    if(!_adminBase){ try{ localStorage.removeItem("lanac:aj:signalements_admin:"+me.id); }catch(e){} }
    (me?.role!=="Admin"?listeAJour({client,rpc:"reports_a_jour",nom:"signalements",uid:me.id,octetsMax:250000}):_adminBase?listeAJour({client,rpc:"reports_admin_a_jour",nom:"signalements_admin",uid:me.id,octetsMax:600000}):Promise.resolve(null)).then(rows=>cancelled?null:(rows?poser(rows):lireAvant()));
    return()=>{cancelled=true;};
  },[me?.id,me?.role,me?.isAdmin]);
  // V3-T3b · Profils — sync delta. Colonnes explicites (= exactement ce que profileFromDB consomme) + updated_at.
  // Ne re-télécharge que les profils modifiés depuis la dernière sync ; le cache "lanac:users" sert de base.
  // ── EGRESS P1c · LA SYNCHRO DEVIENT UN PRIVILÈGE ADMIN quand `pg_profiles_lazy` est armé ──
  //  Mesure fondatrice (banc 5k) : cette synchro = 734,9 Ko décodés / 68 % du boot / 8 appels,
  //  linéaire avec le nombre de membres (~7,3 Mo à 50 000). Le contrat 1a a établi que tous
  //  les consommateurs non-admin sont des lookups PAR ID, servis par l'hydratation existante
  //  (mergeFeedSellers / mergeConvoParticipants / hydrateProfiles). Seul l'écran admin liste
  //  les comptes : à ON, lui seul paie la table. Drapeau OFF ⇒ une exécution au montage,
  //  STRICTEMENT identique à avant (usersSyncRanRef neutralise la nouvelle dépendance de rôle).
  const usersSyncRanRef=useRef(false);
  // ⚠️ L'annulation reste liée au DÉMONTAGE seul : avec la dépendance de rôle, un cleanup
  //  par-exécution avorterait la synchro en vol dès que le rôle arrive (undefined→"Particulier"),
  //  drapeau OFF compris — la régression exacte que ce chantier s'interdit.
  const usersSyncUnmountedRef=useRef(false);
  useEffect(()=>()=>{usersSyncUnmountedRef.current=true;},[]);
  useEffect(()=>{
    const client=lanacSupabaseClient();
    if(!client)return;
    (async()=>{
    // ── EGRESS B5 (01/08/2026) · LA PORTE S'ÉVALUE APRÈS LES DRAPEAUX, PAS AVANT ──────────
    //  Sur une PREMIÈRE visite (miroir absent), cette synchro partait AVANT la réponse
    //  `app_flags` ⇒ la porte lisait « OFF » et l'appareil payait l'annuaire COMPLET une
    //  fois (734,9 Ko mesurés au banc — ~7,3 Mo à 50 000 membres). C'est une synchro
    //  d'ARRIÈRE-PLAN : rien ne justifiait cette course. Le loquet attend la réponse des
    //  drapeaux (une ligne, en tête de boot) UNIQUEMENT quand le miroir est absent —
    //  un appareil instruit ne paie RIEN, et le fail-open (2 s) rend l'ancien monde intact.
    await lanacFlagsFirstBootWait("lanac:profilesLazyServer");
    if(usersSyncUnmountedRef.current)return;
    if(lanacProfilesLazyOn()&&me?.role!=="Admin"){
      // Non-admin paresseux : AUCUNE synchro d'annuaire. On retire aussi le POINTEUR de sync :
      // le cache étant désormais borné (LRU 300), un delta repris plus tard sur cette base
      // partielle (drapeau rendu OFF, ou compte devenu admin) fabriquerait un annuaire
      // silencieusement INCOMPLET — pointeur absent ⇒ resynchro PLEINE, jamais de trou.
      try{localStorage.removeItem(CACHE_USERS_SYNC_KEY);}catch{}
      return;
    }
    if(usersSyncRanRef.current)return;                   // une seule synchro par session (fidèle aux deps [] d'origine)
    usersSyncRanRef.current=true;
    const cancelled=()=>usersSyncUnmountedRef.current;
    await (async()=>{
      const PROFILE_COLS="id,name,email,phone,avatar,country,city,color,role,verified,email_verified,phone_verified,blocked,is_admin,auth_provider,apple_private_relay,accepts_offers,publish_burst_bypass,profile_incomplete,notif_push,notif_email,notif_in_app,notif_prefs,cookies,created_at,updated_at,trust_score,trust_boost,boost_tier,trust_breakdown,is_trusted,success_tx,tier";
      let baseArr=[],lastSync=null;
      try{const c=JSON.parse(localStorage.getItem(CACHE_USERS_CACHE_KEY)||"null");if(Array.isArray(c))baseArr=c;}catch{}
      try{lastSync=localStorage.getItem(CACHE_USERS_SYNC_KEY)||null;}catch{}
      const haveCache=baseArr.length>0&&!!lastSync;
      const res=await fetchTableDelta(client,"profiles_secure",PROFILE_COLS,haveCache?lastSync:null);
      if(cancelled()||!res)return;
      const changed=res.rows.map(profileFromDB).filter(Boolean);
      let merged;
      if(res.full){
        if(!changed.length)return; // table vide / pas de droits → on garde l'état courant (démo/cache)
        merged=changed;
      }else{
        const byId=new Map(baseArr.map(u=>[String(u.id),u]));
        changed.forEach(u=>byId.set(String(u.id),u));
        merged=[...byId.values()].filter(u=>res.allIds.has(String(u.id))); // purge des profils supprimés
      }
      if(cancelled())return;
      setAllUsers(merged);
      let okCache=false;
      try{localStorage.setItem(CACHE_USERS_CACHE_KEY,JSON.stringify(merged));okCache=true;}catch(e){try{localStorage.removeItem(CACHE_USERS_CACHE_KEY);}catch{}}
      // Le pointeur de sync n'est posé QUE si le cache complet a bien été écrit → jamais de delta sur un cache partiel.
      try{if(okCache&&res.maxSync)localStorage.setItem(CACHE_USERS_SYNC_KEY,res.maxSync);else localStorage.removeItem(CACHE_USERS_SYNC_KEY);}catch{}
    })();
    })(); // B5 : fin de l'enveloppe « attendre les drapeaux avant la porte »
  },[me?.role]);
  // ── C1 (17/07/2026) — mergeConvoParticipants remonté de App(), verbatim.
  //  Il hydrate `allUsers` (domaine de CE hook) avec les profils des interlocuteurs de
  //  conversation ; il prend sa liste EN PARAMÈTRE (ne lit pas `convos`). convoPartFetchedRef
  //  (dédup de session) le suit. RETOURNÉ : l'effet déclencheur RESTE dans App() — il a
  //  besoin de `convos` (useMessagingState, appelé APRÈS ce hook) ⇒ câblage inter-domaines
  //  de composition root, non déplaçable sans réordonner les hooks.
  const convoPartFetchedRef=useRef(new Set()); // dédup : ids d'interlocuteurs déjà demandés cette session
  const mergeConvoParticipants=useCallback(convoList=>{
    try{
      const client=lanacSupabaseClient(); if(!client) return;
      const UUID_RE=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const have=new Set((allUsersRef.current||[]).map(u=>String(u.id)));
      const ids=[...new Set((convoList||[]).flatMap(c=>Array.isArray(c?.participants)?c.participants:[]).filter(Boolean).map(String))]
        .filter(id=>id!==String(me?.id)&&UUID_RE.test(id)&&!have.has(id)&&!convoPartFetchedRef.current.has(id));
      if(!ids.length) return;
      ids.forEach(id=>convoPartFetchedRef.current.add(id));
      client.from("profiles_secure").select(FEED_PROFILE_COLS).in("id",ids).then(({data})=>{
        if(Array.isArray(data)&&data.length){const mapped=data.map(profileFromDB).filter(Boolean);setAllUsers(prev=>{const byId=new Map(prev.map(u=>[String(u.id),u]));mapped.forEach(u=>byId.set(String(u.id),u));return [...byId.values()];});}
        // Robustesse (P5) : tout id NON revenu (échec transitoire / réponse vide) est déverrouillé → rechargé au prochain
        // changement de convos. Sinon un profil resté absent affichait « votre interlocuteur » de façon FIGÉE.
        const got=new Set((Array.isArray(data)?data:[]).map(r=>String(r.id)));
        ids.forEach(id=>{if(!got.has(id))convoPartFetchedRef.current.delete(id);});
      },()=>{ids.forEach(id=>convoPartFetchedRef.current.delete(id));});
    }catch(e){}
  },[me?.id]);
  // ── EGRESS P1c · hydratation GÉNÉRIQUE par id — même motif que mergeConvoParticipants,
  //  pour les besoins ponctuels (liste d'abonnés à l'ouverture, résultats de recherche de
  //  membres). Dédup de session, plafond 50 ids/appel, ids non revenus déverrouillés.
  const profileHydrateRef=useRef(new Set());
  const hydrateProfiles=useCallback(rawIds=>{
    try{
      const client=lanacSupabaseClient(); if(!client) return;
      const UUID_RE=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const have=new Set((allUsersRef.current||[]).map(u=>String(u.id)));
      const ids=[...new Set((rawIds||[]).filter(Boolean).map(String))]
        .filter(id=>UUID_RE.test(id)&&!have.has(id)&&!profileHydrateRef.current.has(id)).slice(0,50);
      if(!ids.length) return;
      ids.forEach(id=>profileHydrateRef.current.add(id));
      client.from("profiles_secure").select(FEED_PROFILE_COLS).in("id",ids).then(({data})=>{
        if(Array.isArray(data)&&data.length){const mapped=data.map(profileFromDB).filter(Boolean);setAllUsers(prev=>{const byId=new Map(prev.map(u=>[String(u.id),u]));mapped.forEach(u=>byId.set(String(u.id),u));return [...byId.values()];});}
        const got=new Set((Array.isArray(data)?data:[]).map(r=>String(r.id)));
        ids.forEach(id=>{if(!got.has(id))profileHydrateRef.current.delete(id);});
      },()=>{ids.forEach(id=>profileHydrateRef.current.delete(id));});
    }catch(e){}
  },[]);
  // ── C3 (17/07/2026) — updateMe (mise à jour du profil de l'utilisateur) remonté de App(),
  //  VERBATIM. Attribution : il MUTE allUsers/setAllUsers (domaine de CE hook) ET me/setMe
  //  (useSession) ; useAdminData est le DERNIER hook appelé parmi ceux dont updateMe dépend
  //  ⇒ me est déjà le param, allUsers/setAllUsers sont internes, setMe/notify/lang arrivent en
  //  PORTS. RETOURNÉ : consommé par le JSX (ProfilePage onUpdate, ProfileCompletionModal onSave).
  //  Prouvé de bout en bout par le filet e2e S25 (édition du nom + restauration).
  const updateMe=patch=>{
    //  (B-HORS-LIGNE-1, 03/11/2026) la carte ne donne aucun droit : rien ne s'écrit depuis elle (un champ qu'elle n'aurait pas
    //   — ou plus — écraserait le vrai) ; le profil frais arrive d'ordinaire en moins d'une seconde
    if(me?.provisoire){notify(profilProvisoireTexte(lang),"var(--color-primary)");return false;}
    const finalPatch={...patch};
    //  (B-MOTS, 02/11/2026) le NOM est jugé comme en base : seulement s'il CHANGE (une bio n'est jamais enregistrée — elle n'est plus jugée)
    const profileIssue=finalPatch.name!==undefined&&finalPatch.name!==me.name?safetyTextIssue(finalPatch.name):null;
    if(profileIssue){notify(nomRefuseTexte(profileIssue,lang),"var(--color-primary)");return false;}
    if(finalPatch.email!==undefined){
      const cleanEmail=finalPatch.email.trim().toLowerCase();
      if(!validEmail(cleanEmail)){notify(tx("tax.email-invalide_7ecdc5",lang),"var(--color-primary)");return false;}
      if(allUsers.some(u=>u.id!==me.id&&(u.email||"").toLowerCase()===cleanEmail)){notify(tx("tax.cet-email-est-deja-utili_eb9a12",lang),"var(--color-primary)");return false;}
      if(cleanEmail!==me.email){
        finalPatch.email=cleanEmail;
        finalPatch.emailVerified=false;
      }
    }
    if(finalPatch.phone!==undefined&&finalPatch.phone.trim()!==(me.phone||"").trim()&&!patch.phoneVerified){
      finalPatch.phoneVerified=false;
    }
    const updated={...me,...finalPatch};
    const updatedUsers=allUsers.map(u=>u.id===me.id?updated:u);
    setMe(updated);
    setAllUsers(updatedUsers);
    try{localStorage.setItem("lanac:users",JSON.stringify(updatedUsers));}catch{}
    const _uc=lanacSupabaseClient();
    // L'avatar (photo de profil) en base64 ne doit JAMAIS être écrit en base : on l'upload sur R2
    // en arrière-plan puis on remplace par l'URL. L'aperçu base64 reste affiché instantanément.
    const avatarB64=typeof finalPatch.avatar==="string"&&finalPatch.avatar.startsWith("data:image");
    const prevAvatar=me.avatar;
    let _save=Promise.resolve({ok:true,error:null});
    if(_uc){
      const _dbp=profileToDB(finalPatch);
      if(avatarB64) delete _dbp.avatar; // l'URL R2 arrivera juste après (ci-dessous)
      if(Object.keys(_dbp).length>0)_save=_uc.from("profiles").update(_dbp).eq("id",me.id).then(({error})=>{
        //  (B-MOTS, 02/11/2026) la base refuse un nom qui porte une expression BLOQUÉE (un appareil d'avant, une liste plus récente) : RIEN n'a été écrit —
        //   tout ce que l'écran avait posé d'avance revient (me, l'annuaire, sa copie locale), et c'est dit ; la console reste propre (une règle)
        const _mot=texteRefuse(error);
        if(_mot!==null){
          const avant=Object.fromEntries(Object.keys(finalPatch).map(k=>[k,me[k]]));
          setMe(m=>m&&m.id===me.id?{...m,...avant}:m);
          setAllUsers(us=>us.map(u=>u.id===me.id?{...u,...avant}:u));
          try{const cur=JSON.parse(localStorage.getItem("lanac:users")||"[]");localStorage.setItem("lanac:users",JSON.stringify(cur.map(u=>u.id===me.id?{...u,...avant}:u)));}catch{}
          notify&&notify(nomRefuseTexte(_mot,lang),"var(--color-primary)");
          return{ok:false,error};
        }
        if(error){
          console.error("[LANAC] profile patch error:",error.message,error.code);
          // EGRESS P1c · sous annuaire paresseux, la dédup email locale ne voit que le CACHE (LRU 300) :
          // l'autorité est l'index serveur uniq_profiles_email_ci (vérifié par la migration 1b). Un 23505
          // sur un changement d'email ANNULE l'optimiste et explique — l'écran ne ment jamais.
          if(lanacProfilesLazyOn()&&error.code==="23505"&&finalPatch.email!==undefined){
            setMe(m=>m&&m.id===me.id?{...m,email:me.email,emailVerified:me.emailVerified}:m);
            setAllUsers(us=>us.map(u=>u.id===me.id?{...u,email:me.email,emailVerified:me.emailVerified}:u));
            notify&&notify(tx("tax.cet-email-est-deja-utili_eb9a12",lang),"var(--color-primary)");
          }
        }
        return{ok:!error,error:error||null};},err=>{console.error("[LANAC] profile patch exception:",err?.message||err);return{ok:false,error:err};});
      //  (B-MOTS, 02/11/2026) un nom REFUSÉ par la base ne laisse pas partir l'adresse (rien n'est écrit) ; toute autre issue : comme avant
      if(finalPatch.email!==undefined&&finalPatch.email!==me.email)_save.then(r=>{if(!(r&&r.ok===false&&texteRefuse(r.error)!==null))_uc.auth.updateUser({email:finalPatch.email}).catch(()=>{});});
    }
    // Avatar base64 → upload R2 (repli base64 si échec), puis MAJ état + base + purge de l'ancienne photo.
    if(avatarB64){
      (async()=>{
        try{
          //  (B-MOTS, 02/11/2026) un nom REFUSÉ par la base ne laisse pas partir la photo (l'aperçu est déjà revenu) ; toute autre issue : comme avant
          const _r=await _save;
          if(_r&&_r.ok===false&&texteRefuse(_r.error)!==null) return;
          let uid=null;try{uid=(await _uc?.auth.getSession())?.data?.session?.user?.id;}catch(e){}
          uid=uid||me.id;
          const stored=await ensureStoredAvatar(finalPatch.avatar,uid);
          // Succès = ensureStoredAvatar a renvoyé une clé R2 (ou une URL de repli), donc PAS le base64 d'origine.
          const uploadedOk=typeof stored==="string"&&!!stored&&!/^data:/.test(stored);
          const finalAvatar=uploadedOk?stored:finalPatch.avatar;
          // État local : on affiche toujours la photo (clé R2 si l'upload a réussi, sinon l'aperçu réduit local).
          setMe(m=>m&&m.id===me.id?{...m,avatar:finalAvatar}:m);
          setAllUsers(us=>us.map(u=>u.id===me.id?{...u,avatar:finalAvatar}:u));
          try{const cur=JSON.parse(localStorage.getItem("lanac:users")||"[]");localStorage.setItem("lanac:users",JSON.stringify(cur.map(u=>u.id===me.id?{...u,avatar:finalAvatar}:u)));}catch{}
          // Base : on ne persiste QUE la clé R2. Si l'upload a échoué, on NE met PAS de base64 en base
          // (sinon egress lourd à chaque chargement) → la photo reste visible en local et resync plus tard.
          if(_uc&&uploadedOk)_uc.from("profiles").update({avatar:finalAvatar}).eq("id",me.id).catch(()=>{});
          const oldKey=r2KeyOf(prevAvatar);
          if(oldKey&&uploadedOk&&finalAvatar!==prevAvatar)deleteFromR2(oldKey); // purge de l'ancienne photo R2 seulement si la nouvelle est bien stockée
        }catch(e){console.error("[LANAC] avatar upload error:",e?.message||e);}
      })();
    }else if(finalPatch.avatar!==undefined&&finalPatch.avatar!==prevAvatar){
      // Avatar changé pour une clé/URL directe ou supprimé ("") → purge l'ancienne photo R2 si besoin — (B-MOTS) après le OUI de la base seulement.
      const oldKey=r2KeyOf(prevAvatar);
      if(oldKey)_save.then(r=>{if(!(r&&r.ok===false&&texteRefuse(r.error)!==null))deleteFromR2(oldKey);});
    }
    return _save;
  };
  // ── D1 (17/07/2026) — restoreProfileFromSession (upsert/restauration du profil au login)
  //  remonté de App(), VERBATIM. Même attribution qu'updateMe : mute setAllUsers (CE hook) ET
  //  me/setMe + setPendingSignup/setAuthEmailStatus (useSession) ⇒ useAdminData est le dernier
  //  hook appelé parmi ceux dont il dépend. RETOURNÉ : consommé par les effets/handlers AUTH
  //  d'App (onAuthStateChange, boot, OTP) qui RESTENT en composition root. Couvert e2e S6/S16/S17.
  // ── EGRESS (banc 5k ressuscité, 02/09) : les évènements d'auth du boot (INITIAL_SESSION,
  //  SIGNED_IN, TOKEN_REFRESHED…) rejouaient CHACUN la restauration complète — 2 lectures
  //  `profiles_secure select=*` par passage (une seule depuis le 27/09 quand rien n'est écrit),
  //  5 requêtes IDENTIQUES mesurées au boot froid
  //  (le plafond profiles_secure=4 du cliquet a mordu). Dédup à DEUX étages, `session` seul :
  //   · en vol : les appels concurrents du même uid partagent LA même promesse ;
  //   · accompli : même uid + même clé de session (confirmé/provider) + `me` DÉJÀ ce compte
  //     ⇒ no-op. La garde `me` (ref, jamais la closure) interdit tout skip après un sign-out.
  //  `callback`/`otp` passent TOUJOURS en plein (cleanAuthUrl, statut e-mail frais).
  const _restoreMeIdRef=useRef(null);
  _restoreMeIdRef.current=me?.id||null;
  const _restoreInFlightRef=useRef(new Map());  // uid → promesse en cours (source "session")
  const _restoreDoneRef=useRef(new Map());      // uid → clé de session du dernier succès
  const restoreProfileFromSession=async(session,source="session")=>{
    const authUser=session?.user;
    const cleanEmail=(authUser?.email||"").trim().toLowerCase();
    if(!authUser||!validEmail(cleanEmail)) return null;
    const client=lanacSupabaseClient();
    if(!client) return null;
    if(source==="session"){
      const _cle=`${cleanEmail}|${authUser.email_confirmed_at||authUser.confirmed_at||""}|${(authUser.app_metadata?.provider||"email")}`;
      if(_restoreDoneRef.current.get(authUser.id)===_cle&&_restoreMeIdRef.current===authUser.id) return me;
      const _enVol=_restoreInFlightRef.current.get(authUser.id);
      if(_enVol) return _enVol;
      const _p=(async()=>{
        try{
          const _r=await _restoreProfileFromSessionPlein(session,source,client,authUser,cleanEmail);
          if(_r) _restoreDoneRef.current.set(authUser.id,_cle);
          return _r;
        }finally{_restoreInFlightRef.current.delete(authUser.id);}
      })();
      _restoreInFlightRef.current.set(authUser.id,_p);
      return _p;
    }
    return _restoreProfileFromSessionPlein(session,source,client,authUser,cleanEmail);
  };
  const _restoreProfileFromSessionPlein=async(session,source,client,authUser,cleanEmail)=>{
    //  (B-HORS-LIGNE-1, 03/11/2026) l'application est visible pendant la restauration (la carte) : une sortie peut survenir
    //   pendant que la lecture voyage — sa réponse ne réinstalle jamais le membre parti (l'époque avance à chaque oubli de la carte)
    const _epoque=lanacEpoqueSession();
    const pending=readPendingSignupMeta(cleanEmail)||{};
    const state={...readOnboardingState(cleanEmail),...readOnboardingState(authUser.id)};
    const emailVerified=!!(authUser.email_confirmed_at||authUser.confirmed_at);
    const provider=(authUser.app_metadata?.provider||authUser.identities?.[0]?.provider||"email").toLowerCase();
    const isOAuthProvider=provider==="google"||provider==="apple";
    const isAppleRelay=provider==="apple"&&cleanEmail.endsWith("@privaterelay.appleid.com");
    // Refactor 05/08 : le littéral local est remplacé par LE constructeur unique (05) —
    //  même résultat champ à champ pour ce chemin, prouvé par tests/profil-de-base.test.mjs.
    const baseProfile=lanacProfilDeBase(authUser,{pending});
    const {data:existing,error:existingErr}=await client.from("profiles_secure").select("*").eq("id",authUser.id).maybeSingle().catch(error=>({data:null,error}));
    if(existingErr){
      // ⚠️ Un échec de LECTURE (réseau transitoire, QUIC coupé…) n'est PAS une absence de
      //  profil : poursuivre déclencherait le chemin « création » — un upsert voué au 403
      //  (aucun GRANT INSERT sur profiles, le trigger serveur est le seul créateur) et un
      //  toast d'erreur trompeur alors que la session, elle, est saine. On se retire ;
      //  l'appelant (onAuthStateChange/boot) repassera ici au prochain évènement de session.
      console.error("[LANAC] restoreProfileFromSession profile fetch error:",existingErr.message||existingErr);
      return null;
    }
    // (02/10) LA VILLE DE L'INSCRIPTION. Le profil naît au serveur avec la ville par défaut de la base ; celle que le membre
    //  a donnée en s'inscrivant voyage avec le COMPTE (user_metadata — elle survit à un lien de confirmation ouvert sur un
    //  autre appareil). Elle est posée UNE fois, tant que le profil n'a pas de ville CHOISIE : jamais par-dessus un choix.
    const _meta=authUser.user_metadata||{};
    const _nb=v=>(v==null||v==="")?NaN:Number(v);
    const _lieuInscription=(existing&&!profilLieuChoisi(profileFromDB(existing))&&typeof _meta.city==="string"&&_meta.city.trim()&&Number.isFinite(_nb(_meta.cityLat))&&Number.isFinite(_nb(_meta.cityLng)))
      ?{city:_meta.city.trim().slice(0,200),country:String(_meta.country||"lu").toLowerCase().slice(0,80),city_lat:_nb(_meta.cityLat),city_lng:_nb(_meta.cityLng)}:null;
    let _ecrit=false;
    if(!existing){
      _ecrit=true;
      const {error:upsertErr}=await client.from("profiles").upsert(baseProfile,{onConflict:"id"}).catch(error=>({error}));
      if(upsertErr){
        console.error("[LANAC] restoreProfileFromSession profile upsert error:",upsertErr.message||upsertErr);
        notify(tx("tax.connexion-reussie-mais-l_338a9e",lang),"var(--color-error)");
      }
    }else if(_lieuInscription||(emailVerified&&!existing.email_verified)||(isOAuthProvider&&(existing.auth_provider!==provider||!!existing.apple_private_relay!==isAppleRelay))){
      const restoreUpdates={...(_lieuInscription||{})};
      if(emailVerified&&!existing.email_verified) restoreUpdates.email_verified=true;
      if(isOAuthProvider){
        restoreUpdates.auth_provider=provider;
        restoreUpdates.apple_private_relay=isAppleRelay;
      }
      _ecrit=true;
      const {error:updateErr}=await client.from("profiles").update(restoreUpdates).eq("id",authUser.id).catch(error=>({error}));
      if(updateErr) console.error("[LANAC] restoreProfileFromSession profile update error:",updateErr.message||updateErr);
    }
    // ⭐ 27/09/2026 — UNE LECTURE QUAND RIEN N'A ÉTÉ ÉCRIT. La relecture ne sert qu'à voir ce que la création ou la mise à
    //  jour ci-dessus viennent d'écrire. Sans écriture, elle rendait la MÊME ligne : une seconde attente sur le chemin de
    //  l'ouverture (mesuré le 26/09 : 4,9 s puis 1,5 s — le filet de boot était déjà parti) et un select * de plus à
    //  chaque démarrage de chaque membre. `existing` EST cette ligne (même vue, même filtre).
    const {data:profileRow}=_ecrit?await client.from("profiles_secure").select("*").eq("id",authUser.id).single().catch(()=>({data:null})):{data:existing};
    const restored={...(profileFromDB(profileRow)||profileFromDB(baseProfile)||{
      id:authUser.id,name:baseProfile.name,email:cleanEmail,role:baseProfile.role,emailVerified
    })};
    restored.emailVerified=emailVerified||!!restored.emailVerified;
    restored.pendingVerification=!restored.emailVerified;
    restored.pendingEmail=restored.pendingVerification?cleanEmail:null;
    restored.termsAccepted=state.termsAccepted!==false;
    restored.welcomeCompleted=!!state.welcomeCompleted;
    //  (B-MOTS · 1b, 02/11/2026) un compte e-mail SANS nom (la base a neutralisé, à la naissance, un nom qui n'est pas accepté) est incomplet : la fiche
    //   « compléter le profil » lui redemande un nom — avant, un compte e-mail n'était jamais incomplet
    restored.profileIncomplete=isOAuthProvider?restored.profileIncomplete!==false:!String(restored.name||"").trim();
    //  (B-HORS-LIGNE-1) un profil reconstruit SANS ligne en base (repli du profil de base) n'est jamais gardé en carte
    restored.sansLigne=!profileRow;
    if(_epoque!==lanacEpoqueSession()) return null;
    //  (relecture APRÈS, D6) `gen` (les chargements rejoués après une ouverture sans réseau) traverse une restauration complète ultérieure
    //   (code, lien de confirmation) : sans lui, il retomberait à zéro et rejouerait tout une fois de plus
    setMe(m=>m&&m.id===restored.id&&m.gen?{...restored,gen:m.gen}:restored);
    setAllUsers(p=>[...p.filter(u=>u.id!==restored.id&&u.id!==`pending:${cleanEmail}`),restored]);
    mergeOnboardingState(restored.id,{termsAccepted:restored.termsAccepted,welcomeCompleted:restored.welcomeCompleted,pendingEmail:cleanEmail,pendingVerification:restored.pendingVerification});
    mergeOnboardingState(cleanEmail,{termsAccepted:restored.termsAccepted,welcomeCompleted:restored.welcomeCompleted,pendingEmail:cleanEmail,pendingVerification:restored.pendingVerification});
    if(restored.emailVerified){
      clearPendingSignupMeta(cleanEmail);
      setPendingSignup(null);
      setAuthEmailStatus(tx("tax.email-valide-votre-compt_84253d",lang));
    }
    if(source==="callback") cleanAuthUrl();
    return restored;
  };
  return {allUsers,setAllUsers,reports,setReports,allUsersRef,mergeConvoParticipants,hydrateProfiles,updateMe,restoreProfileFromSession};
}
export { useAdminData };
